import type { JSX } from 'react';
import { Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { NavLink } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';
import { canAccessWebModule, type ModuleKey } from '../auth/moduleAccessPolicy';

const PRIMARY_DESTINATIONS: Array<{ module: ModuleKey; label: string; to: string }> = [
  { module: 'shipments', label: 'Open shipments', to: '/shipments' },
  { module: 'holdingUnits', label: 'Open holding units', to: '/holding-units' },
  { module: 'cash', label: 'Open cash', to: '/cash' },
  { module: 'customers', label: 'Open customers', to: '/customers' },
  { module: 'employees', label: 'Open employees', to: '/employees' },
];

export function HomePage(): JSX.Element {
  const { currentUser } = useAuth();
  const activeCompanyName = currentUser?.activeCompany?.company.name ?? 'your company';
  const primaryDestination = PRIMARY_DESTINATIONS.find((destination) =>
    canAccessWebModule(currentUser, destination.module),
  );
  const canOpenCompanySettings = canAccessWebModule(currentUser, 'company');

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4">Company workspace</Typography>
        <Typography variant="body1" color="text.secondary">
          Work in {activeCompanyName} with the modules available to your account.
        </Typography>
        {currentUser && (
          <Chip
            label={`Signed in as ${currentUser.email}`}
            color="primary"
            variant="outlined"
            sx={{ alignSelf: 'flex-start' }}
          />
        )}
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="h6">Operations</Typography>
                <Typography variant="body2" color="text.secondary">
                  Start from the navigation or open your first available operations module.
                </Typography>
              </Stack>
              {primaryDestination ? (
                <Button
                  component={NavLink}
                  to={primaryDestination.to}
                  variant="contained"
                  color="primary"
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {primaryDestination.label}
                </Button>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No operations modules are available for this account yet.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="h6">Workspace</Typography>
                <Typography variant="body2" color="text.secondary">
                  Review the active company, billing state, and workspace options.
                </Typography>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button component={NavLink} to="/select-company" variant="outlined">
                  Select company
                </Button>
                <Button component={NavLink} to="/billing" variant="outlined">
                  Billing
                </Button>
                {canOpenCompanySettings && (
                  <Button component={NavLink} to="/admin/company" variant="outlined">
                    Company settings
                  </Button>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
}
